import React, { useState, useEffect } from "react";
import { XMLParser } from "fast-xml-parser";
import Sidebar from "../Sidebar";
import { getDB, dbPut } from "../../db";
import axios from "axios";
import {
  Save,
  Upload,
  File,
  RefreshCcw,
  CheckCircle,
  XCircle,
} from "lucide-react";

import {
  ImportWrapper,
  Content,
  Card,
  PageBody,
  Message,
  Button,
} from "./ImportComponentStyles";
import FinancialStatementImport from "./FinancialStatementImport";
import styled, { keyframes } from "styled-components";
import TopBar from "../TopBar";
import { jwtDecode } from "jwt-decode";

const DEMO_MAX_IMPORTS = 99;

const pulse = keyframes`
  0% { transform: translateY(0); opacity: .9; }
  50% { transform: translateY(-2px); opacity: 1; }
  100% { transform: translateY(0); opacity: .9; }
`;

export const DropArea = styled.div`
  /* layout */
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 10px;

  /* size */
  min-height: 200px;
  padding: 28px;

  /* visuals */
  border: 3px dashed #1e90ff;
  border-radius: 14px;
  background: linear-gradient(180deg, #ffffff 0%, #fafbff 100%);
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease,
    transform 0.08s ease;
  cursor: pointer;

  /* text defaults (overridden by inner elements) */
  color: #334155;
  text-align: center;

  /* hover */
  &:hover {
    background: #f5f9ff;
    box-shadow: 0 6px 20px rgba(30, 144, 255, 0.1);
  }

  /* keyboard accessibility */
  &:focus {
    outline: none;
  }
  &:focus-visible {
    box-shadow: 0 0 0 4px rgba(30, 144, 255, 0.25);
  }

  /* drag-over state */
  &.dragover {
    background: #eef6ff;
    border-color: #0b74e5;
    box-shadow: 0 8px 24px rgba(11, 116, 229, 0.18);
    transform: translateY(-1px);
  }
`;

export const DropIcon = styled(Upload)`
  width: 56px;
  height: 56px;
  opacity: 0.95;
  animation: ${pulse} 1.4s ease-in-out infinite;
`;

export const DropTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
  letter-spacing: 0.1px;
`;

export const DropSubtitle = styled.div`
  font-size: 14px;
  color: #475569;
  line-height: 1.5;
`;

const TabBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
  padding: 6px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
`;

const TabButton = styled.button`
  padding: 10px 16px;
  border: 0;
  border-radius: 8px;
  background: ${(props) => (props.$active ? "#1e90ff" : "transparent")};
  color: ${(props) => (props.$active ? "#ffffff" : "#334155")};
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: ${(props) => (props.$active ? "#0b74e5" : "#eaf3ff")};
  }

  &:focus-visible {
    outline: 3px solid rgba(30, 144, 255, 0.28);
    outline-offset: 2px;
  }
`;

const ImportComponent = () => {
  const [message, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState("import");
  const [progress, setProgress] = useState(0); // Track posting progress
  const [isPosting, setIsPosting] = useState(false); // Track if posting is active
  const [importCompleted, setImportCompleted] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [fetchedSprzedazData, setFetchedSprzedazData] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [filesToImport, setFilesToImport] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);

  const [fetchedZakupData, setFetchedZakupData] = useState([]);
  const [isLoadingZakup, setIsLoadingZakup] = useState(false);



  // Track DEMO usage
const [demoInfo, setDemoInfo] = useState({
  loading: true,
  isDemo: false,
  used: 0,
  left: DEMO_MAX_IMPORTS,
});


function getLicenseTypeFromToken() {
  const token = localStorage.getItem("token");
  if (!token) return null;
  try {
    const d = jwtDecode(token);
    return d.LicenseType || d.licenseType || null;
  } catch {
    return null;
  }
}



useEffect(() => {
  (async () => {
    const u = getUserFromToken();
    if (!u) {
      setDemoInfo({ loading: false, isDemo: false, used: 0, left: DEMO_MAX_IMPORTS });
      return;
    }

    // License type: prefer local user object, fallback to JWT
    const licenseTypeRaw =
      u.licenseType || u.LicenseType || getLicenseTypeFromToken() || "";

    const isDemo = String(licenseTypeRaw).toUpperCase().includes("DEMO");

    // Count history rows belonging to this user (same endpoint you use elsewhere)
    let used = 0;
    try {
      const res = await axios.get(
        "https://server229256.nazwa.pl/api/import-history",
        { headers: { Authorization: localStorage.getItem("token") }, timeout: 20000 }
      );
      const history = Array.isArray(res.data) ? res.data : [];
      used = history.filter(h => h.user && u.Email && h.user === u.Email).length;
    } catch {
      used = 0;
    }

    const left = isDemo ? Math.max(0, DEMO_MAX_IMPORTS - used) : Infinity;
    setDemoInfo({ loading: false, isDemo, used: isDemo ? used : 0, left });
  })();
}, []);



  const handleDrop = async (event) => {
    event.preventDefault();
    setIsDragOver(false);

    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) {
      await handleFileChange({ target: { files } }); // reuse your existing handler
    }
  };

  const getYearMonthFromHeader = (naglowek = {}) => {
    // Newer schemas
    let year = Number(naglowek?.Rok ?? 0);
    let month = Number(naglowek?.Miesiac ?? 0);

    // Older schemas (2016) -> DataOd / DataDo
    if (!year || !month) {
      const dataOd = naglowek?.DataOd || naglowek?.["tns:DataOd"]; // be tolerant
      const dataDo = naglowek?.DataDo || naglowek?.["tns:DataDo"];

      // Prefer deriving from DataOd; fallback to DataDo if needed
      const from = dataOd ? new Date(dataOd) : dataDo ? new Date(dataDo) : null;
      if (from && !isNaN(from.getTime())) {
        year = from.getFullYear();
        month = from.getMonth() + 1; // 1..12
      }
    }

    // Normalize to integers or 0
    year = Number.isFinite(year) ? year : 0;
    month = Number.isFinite(month) ? month : 0;

    return { year, month };
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const extractField = (value) => {
    if (typeof value === "object" && value !== null) {
      return value["#text"] || value["@_poz"] || "-";
    }
    return value || "-";
  };

const parseXMLFile = async (file) => {
  try {
    const text = await file.text();
    console.log("🧩 parseXMLFile() start:", file.name, "size:", text.length);

    const parser = new XMLParser({
      ignoreAttributes: false,
      attributeNamePrefix: "",
      removeNSPrefix: true, // 👈 strips tns:, etd:, etc. — critical for old schema
    });
    const jsonData = parser.parse(text);

    // Normalize root + container so it works for both schemas
    const jpk = jsonData?.JPK || {};
    const container = jpk?.Ewidencja || jpk; // 👈 old files: rows are directly under JPK

    // quick peek
    console.log(
      "🔎 jsonData.JPK?.Ewidencja keys:",
      Object.keys(jpk?.Ewidencja || {})
    );

    // Schema detection log
    const isOld =
      !jpk?.Ewidencja && !!(jpk?.Naglowek?.DataOd || jpk?.Naglowek?.DataDo);
    console.log("🧭 parseXMLFile schema:", isOld ? "old-2016" : "new", {
      hasEwidencja: !!jpk?.Ewidencja,
      keysAtRoot: Object.keys(jpk || {}),
    });

    // ---------- Static ----------
    const naglowekData = jpk?.Naglowek || {};
    const podmiotData = jpk?.Podmiot1 || {};
    const osobaNiefizyczna = podmiotData?.OsobaNiefizyczna || {};
    const { year, month } = getYearMonthFromHeader(naglowekData);

    const staticRow = {
      KodFormularza: naglowekData?.KodFormularza?.["#text"] || "-",
      kodSystemowy: naglowekData?.KodFormularza?.kodSystemowy || "-",
      wersjaSchemy: naglowekData?.KodFormularza?.wersjaSchemy || "-",
      poz: naglowekData?.CelZlozenia?.poz || "-",
      rola: podmiotData?.rola || "-",
      CelZlozenia: naglowekData?.CelZlozenia?.["#text"] || "-",
      NIP:
        osobaNiefizyczna?.NIP ||
        podmiotData?.IdentyfikatorPodmiotu?.NIP ||
        podmiotData?.IdentyfikatorPodmiotu?.["etd:NIP"] ||
        "-",
      PelnaNazwa:
        osobaNiefizyczna?.PelnaNazwa ||
        podmiotData?.IdentyfikatorPodmiotu?.PelnaNazwa ||
        podmiotData?.IdentyfikatorPodmiotu?.["etd:PelnaNazwa"] ||
        "-",
      Email: osobaNiefizyczna?.Email || "-",
      DataWytworzeniaJPK: naglowekData?.DataWytworzeniaJPK || "-",
      NazwaSystemu: naglowekData?.NazwaSystemu || "-",
      KodUrzedu: naglowekData?.KodUrzedu || "-",
      Rok: naglowekData?.Rok || year || "-",
      Miesiac: naglowekData?.Miesiac || month || "-",
      WariantFormularza: naglowekData?.WariantFormularza || "-",
    };

    // ---------- Sprzedaż ----------
    // Old schema: container === jpk; New: container === jpk.Ewidencja
    const sprzedazRowsRaw = container?.SprzedazWiersz || [];
    const sprzedazFieldSet = new Set();
    const sprzedazArray = Array.isArray(sprzedazRowsRaw)
      ? sprzedazRowsRaw
      : sprzedazRowsRaw
      ? [sprzedazRowsRaw]
      : [];

    const sprzedaz = sprzedazArray.map((row) => {
      const out = {};
      Object.keys(row).forEach((key) => {
        out[key] = extractField(row[key]);
        if (key.startsWith("K_")) sprzedazFieldSet.add(key);
      });
      if (!out["KodKrajuNadaniaTIN"] || out["KodKrajuNadaniaTIN"] === "-") {
        if (out["NrKontrahenta"] && /^\d{10}$/.test(out["NrKontrahenta"])) {
          out["KodKrajuNadaniaTIN"] = "PL";
        } else {
          out["KodKrajuNadaniaTIN"] = "OTHER";
        }
      }
      return out;
    });
    const sprzedazColumns = Array.from(sprzedazFieldSet);

    // ---------- Zakupy ----------
const zakupRowsRaw = container?.ZakupWiersz || [];
const zakupFieldSet = new Set();
const zakupyArray = Array.isArray(zakupRowsRaw)
  ? zakupRowsRaw
  : zakupRowsRaw
  ? [zakupRowsRaw]
  : [];

let normalizedCount = 0;

const zakupy = zakupyArray.map((row) => {
  const out = {};
  Object.keys(row).forEach((key) => {
    out[key] = extractField(row[key]);
    if (key.startsWith("K_")) zakupFieldSet.add(key);
  });

  // --- Old-schema normalization (2016):
  // If K_42/K_43 are missing but K_45/K_46 exist, copy them into K_42/K_43
  const hadOldKeys = out["K_45"] !== undefined || out["K_46"] !== undefined;
  const missingNewNet = out["K_42"] === undefined || out["K_42"] === "-";
  const missingNewVat = out["K_43"] === undefined || out["K_43"] === "-";

  if (hadOldKeys && (missingNewNet || missingNewVat)) {
    if (missingNewNet && out["K_45"] !== undefined) {
      out["K_42"] = out["K_45"];
    }
    if (missingNewVat && out["K_46"] !== undefined) {
      out["K_43"] = out["K_46"];
    }
    normalizedCount++;
  }

  // Make sure dynamic columns include both old and new keys
  ["K_42", "K_43", "K_45", "K_46"].forEach((k) => zakupFieldSet.add(k));

  // Country defaulting
  if (!out["KodKrajuNadaniaTIN"] || out["KodKrajuNadaniaTIN"] === "-") {
    if (out["NrDostawcy"] && /^\d{10}$/.test(out["NrDostawcy"])) {
      out["KodKrajuNadaniaTIN"] = "PL";
    } else {
      out["KodKrajuNadaniaTIN"] = "OTHER";
    }
  }
  return out;
});

// keep K_42/K_43 if present anywhere (covered above, but keep as guard)
["K_42", "K_43"].forEach((k) => {
  if (zakupy.some((r) => r[k] !== undefined)) zakupFieldSet.add(k);
});
const zakupColumns = Array.from(zakupFieldSet);

// Helpful logs
if (normalizedCount > 0) {
  console.log(
    `🧰 Zakupy normalization: ${normalizedCount} row(s) mapped K_45→K_42 and/or K_46→K_43`
  );
  console.log("🧰 ZakupColumns after normalization:", zakupColumns);
  // Show one normalized sample
  const sampleNorm = zakupy.find(
    (z) => z["K_45"] !== undefined || z["K_46"] !== undefined
  );
  if (sampleNorm) console.table([sampleNorm]);
}

    // ---------- Logs ----------
    console.log(
      "🧪 Sprzedaz count:",
      sprzedaz.length,
      "Zakupy count:",
      zakupy.length
    );
    if (sprzedaz.length) {
      console.log("🧪 Sprzedaz sample keys:", Object.keys(sprzedaz[0]));
      console.table([sprzedaz[0]]);
    }
    if (zakupy.length) {
      console.log("🧪 Zakupy sample keys:", Object.keys(zakupy[0]));
      console.table([zakupy[0]]);
      console.log("🧪 ZakupColumns:", zakupColumns);
      console.log(
        "🧪 Has K_42:",
        zakupy.some((r) => r["K_42"] !== undefined),
        "Has K_43:",
        zakupy.some((r) => r["K_43"] !== undefined)
      );
      const weirdKeys = Object.keys(zakupy[0]).filter((k) => /:K_4[23]/i.test(k));
      if (weirdKeys.length)
        console.warn("⚠️ Weird namespaced K_* keys found:", weirdKeys);
    }

    return {
      fileName: file.name,
      staticData: [staticRow],
      sprzedaz,
      sprzedazColumns,
      zakupy,
      zakupColumns,
    };
  } catch (error) {
    console.error("❌ parseXMLFile error for", file.name, error);
    throw new Error(`Error processing file ${file.name}: ${error.message}`);
  }
};


const readXmlHeaderAndTypes = async (file) => {
  const text = await file.text();
  const parser = new XMLParser({
    ignoreAttributes: false,
    attributeNamePrefix: "",
    removeNSPrefix: true, // ← IMPORTANT for old tns:* files
  });
  const json = parser.parse(text);

  // Namespace-free now:
  const jpk = json?.JPK || {};

  const naglowek = jpk?.Naglowek || {};
  const { year, month } = getYearMonthFromHeader(naglowek);

  // Old schema (2016) has SprzedazWiersz/ZakupWiersz directly under JPK (no Ewidencja)
  // New schema wraps them under JPK.Ewidencja
  const container = jpk?.Ewidencja || jpk;

  const hasSprzedaz = !!container?.SprzedazWiersz;
  const hasZakup   = !!container?.ZakupWiersz;

  // Helpful logs so you can confirm detection
  const isOld = !jpk?.Ewidencja && !!(naglowek?.DataOd || naglowek?.DataDo);
  console.log("🧭 Schema detection:", {
    schema: isOld ? "old-2016" : "new",
    hasEwidencja: !!jpk?.Ewidencja,
    hasSprzedaz,
    hasZakup,
    headerYear: year,
    headerMonth: month,
    rawHeaderKeys: Object.keys(naglowek || {})
  });

  return { year, month, hasSprzedaz, hasZakup, json };
};


  // Fetch months coverage for a given year
  const fetchCoverageForYear = async (year) => {
    const { data } = await axios.get(
      `https://server229256.nazwa.pl/api/import-coverage?year=${year}`,
      { headers: { Authorization: localStorage.getItem("token") } }
    );
    return data?.months || [];
  };

  // Decide if month/type is already imported
  const checkAlreadyImported = (months, month, hasSprzedaz, hasZakup) => {
    const rec = months.find((m) => m.month === month);
    const alreadySprzedaz = !!(hasSprzedaz && rec?.sprzedaz);
    const alreadyZakupy = !!(hasZakup && rec?.zakupy);
    return { alreadySprzedaz, alreadyZakupy };
  };

  const getUserFromToken = () => {
    const userString = localStorage.getItem("user"); // Get the user data from localStorage
    if (!userString) {
      console.warn("No user data found in localStorage");
      return null;
    }
    try {
      const user = JSON.parse(userString); // Parse the JSON string into a JavaScript object
      if (!user.Email || !user.ID) {
        // Adjust keys to match the actual user object
        console.error("Invalid user data:", user);
        return null;
      }
      return user; // Return the entire user object
    } catch (error) {
      console.error("Error parsing user data:", error.message);
      return null;
    }
  };

  const postData = async (bundle) => {
    const user = getUserFromToken();
    if (!user) {
      setMessage({
        text: "User not authenticated. Please log in.",
        isError: true,
      });
      return;
    }

    if (!bundle?.sprzedaz?.length) {
      // nothing to post for sales
      return;
    }

    await axios.post(
      "https://server229256.nazwa.pl/save-sprzedaz-data",
      {
        staticData: bundle.staticData,
        dynamicSprzedazData: bundle.sprzedaz,
        dynamicColumns: bundle.sprzedazColumns,
        fileName: bundle.fileName,
        user,
      },
      { headers: { Authorization: localStorage.getItem("token") } }
    );

    setMessage({
      text: `Sales saved for "${bundle.fileName}".`,
      isError: false,
    });
  };

const handleFileChange = async (event) => {
  const user = getUserFromToken();
  const rawFiles = Array.from(event.target.files || []);
  if (!rawFiles.length) return;

  // DEMO limit gate — cut selection to remaining slots
  let files = rawFiles;
  if (demoInfo.isDemo) {
    if (demoInfo.left <= 0) {
      setMessage({
        text: `Limit importów DEMO został wykorzystany (${DEMO_MAX_IMPORTS}/${DEMO_MAX_IMPORTS}).`,
        isError: true,
      });
      return;
    }
    if (files.length > demoInfo.left) {
      setMessage({
        text: `Konto DEMO: możesz dodać maks. ${demoInfo.left} plik(i). Pozostałe odrzucono.`,
        isError: true,
      });
      files = files.slice(0, demoInfo.left);
    }
  }

  const coverageCache = new Map();
  const entries = /** @type FileItem[] */ ([]);

  for (const file of files) {
    // (A) Wrong MIME/type
    if (file.type !== "text/xml") {
      entries.push({
        fileName: file.name,
        status: "error",
        error: `Plik nie jest prawidłowym XML.`,
      });
      continue;
    }

    // (B) Duplicate filename on server
    try {
      const resName = await axios.post(
        "https://server229256.nazwa.pl/api/check-file-name",
        { fileName: file.name },
        { headers: { Authorization: localStorage.getItem("token") } }
      );
      if (resName.data.exists) {
        entries.push({
          fileName: file.name,
          status: "error",
          error: `Plik o tej nazwie już istnieje. Import zablokowany.`,
        });
        continue;
      }
    } catch (err) {
      entries.push({
        fileName: file.name,
        status: "error",
        error: `Błąd sprawdzania nazwy pliku: ${err.message}`,
      });
      continue;
    }

    // (C) Read header (year/month & sections)
    let header;
    try {
      header = await readXmlHeaderAndTypes(file);
    } catch {
      entries.push({
        fileName: file.name,
        status: "error",
        error: `Nie udało się odczytać nagłówka.`,
      });
      continue;
    }

    const { year, month, hasSprzedaz, hasZakup } = header;
    if (!year || !month) {
      entries.push({
        fileName: file.name,
        status: "error",
        error: `Brak prawidłowego roku/miesiąca w nagłówku.`,
      });
      continue;
    }

    // (D) Coverage (cached)
    let monthsCoverage = coverageCache.get(year);
    if (!monthsCoverage) {
      try {
        monthsCoverage = await fetchCoverageForYear(year);
        coverageCache.set(year, monthsCoverage);
      } catch {
        entries.push({
          fileName: file.name,
          status: "error",
          error: `Nie udało się pobrać pokrycia dla roku ${year}.`,
        });
        continue;
      }
    }

    // (E) Already imported?
    const { alreadySprzedaz, alreadyZakupy } = checkAlreadyImported(
      monthsCoverage,
      month,
      hasSprzedaz,
      hasZakup
    );

    if (hasSprzedaz && alreadySprzedaz && hasZakup && alreadyZakupy) {
      entries.push({
        fileName: file.name,
        status: "error",
        error: `Dane dla ${year}-${String(month).padStart(
          2,
          "0"
        )} są już zaimportowane (sprzedaż i zakupy).`,
      });
      continue;
    }

    // (F) Parse fully
    try {
      const bundle = await parseXMLFile(file);

      // Optional informational messages (not an error):
      if (hasSprzedaz && alreadySprzedaz && hasZakup && !alreadyZakupy) {
        setMessage({
          text: `Sprzedaż dla ${year}-${String(month).padStart(
            2,
            "0"
          )} już istnieje. Z pliku "${file.name}" zostaną przetworzone tylko zakupy.`,
          isError: false,
        });
      } else if (hasZakup && alreadyZakupy && hasSprzedaz && !alreadySprzedaz) {
        setMessage({
          text: `Zakupy dla ${year}-${String(month).padStart(
            2,
            "0"
          )} już istnieją. Z pliku "${file.name}" zostanie przetworzona tylko sprzedaż.`,
          isError: false,
        });
      }

      entries.push({
        fileName: file.name,
        status: "ready",
        bundle, // store parsed data for posting
      });
      setMessage({
        text: `Plik "${file.name}" został poprawnie odczytany i jest gotowy do zapisania.`,
        isError: false,
      });
    } catch (err) {
      entries.push({
        fileName: file.name,
        status: "error",
        error: `Błąd podczas przetwarzania: ${err.message}`,
      });
    }
  }

  // Replace current list; if you prefer to append, use: setFilesToImport(prev => [...prev, ...entries]);
  setFilesToImport(entries);
};


  const fetchSprzedazData = async () => {
    setIsLoadingData(true);
    try {
      const response = await axios.get(
        "https://server229256.nazwa.pl/api/sprzedaz",
        {
          headers: { Authorization: localStorage.getItem("token") },
        }
      );
      setFetchedSprzedazData(response.data);
      return response.data; // <-- ADD THIS
    } catch (error) {
      console.error("Error fetching Sprzedaz data:", error.message);
      return []; // <-- in case of error
    } finally {
      setIsLoadingData(false);
    }
  };

  const fetchZakupData = async () => {
    setIsLoadingZakup(true);
    try {
      const response = await axios.get(
        "https://server229256.nazwa.pl/api/zakupy",
        {
          headers: { Authorization: localStorage.getItem("token") },
        }
      );
      setFetchedZakupData(response.data);
      return response.data; // <-- ADD THIS
    } catch (error) {
      console.error("Error fetching Zakup data:", error.message);
      return [];
    } finally {
      setIsLoadingZakup(false);
    }
  };

const handlePostData = async () => {
  // collect parsed & ready bundles
  const validBundles = filesToImport
    .filter((f) => f.status === "ready" && f.bundle)
    .map((f) => f.bundle);

  if (!validBundles.length) {
    setMessage({ text: "Brak prawidłowych plików do importu.", isError: true });
    return;
  }

  // DEMO: enforce remaining quota *before* starting
  if (demoInfo?.isDemo && validBundles.length > demoInfo.left) {
    setMessage({
      text: `Konto DEMO: możesz teraz zapisać maks. ${demoInfo.left} plik(i).`,
      isError: true,
    });
    return;
  }

  const user = getUserFromToken();
  if (!user) {
    setMessage({
      text: "Użytkownik nie jest zalogowany. Zaloguj się ponownie.",
      isError: true,
    });
    return;
  }

  setProgress(0);
  setIsPosting(true);
  setCurrentFileIndex(0);

  try {
    const totalFiles = validBundles.length;

    for (let i = 0; i < totalFiles; i++) {
      const bundle = validBundles[i];

      // DEMO: hard gate also inside the loop (in case state changed meanwhile)
      if (demoInfo?.isDemo && demoInfo.left <= 0) {
        setMessage({
          text: "Konto DEMO: limit importów został wyczerpany (2/2).",
          isError: true,
        });
        break;
      }

      setCurrentFileIndex(i + 1);

      // 1) sprzedaż
      await postData(bundle);
      setProgress(((i + 0.33) / totalFiles) * 100);

      // 2) zakupy
      await postZakupData(bundle);
      setProgress(((i + 0.66) / totalFiles) * 100);

      // 3) historia
      await saveImportHistory(bundle.fileName, user.Email);

      // DEMO: decrement remaining after a fully successful file
      if (demoInfo?.isDemo) {
        setDemoInfo((d) => ({
          ...d,
          left: Math.max(0, (d?.left ?? 0) - 1),
        }));
      }

      // On the last file, refresh caches so dashboards are up-to-date
      if (i === totalFiles - 1) {
        const freshSprzedazData = await fetchSprzedazData();
        const freshZakupData = await fetchZakupData();

        await getDB();
        const now = Date.now();
        await dbPut("sales", "allSales", { data: freshSprzedazData, timestamp: now });
        await dbPut("zakupy", "allZakupy", { data: freshZakupData, timestamp: now });
      }

      setProgress(((i + 1) / totalFiles) * 100);
      setMessage({
        text: `✅ Plik "${bundle.fileName}" został pomyślnie przetworzony.`,
        isError: false,
      });
    }

    setImportCompleted(true);
    setMessage({
      text: "🎉 Wszystkie prawidłowe pliki zostały pomyślnie przetworzone i zapisane.",
      isError: false,
    });

    // small UX cleanup & refresh
    setTimeout(() => setMessage({ text: "Czyszczenie... proszę czekać.", isError: false }), 500);
    setTimeout(() => window.location.reload(), 1500);
  } catch (error) {
    console.error("Error in handlePostData:", error);
    setMessage({
      text: `❌ Wystąpił błąd podczas zapisywania danych: ${error.message}`,
      isError: true,
    });
  } finally {
    setIsPosting(false);
    setProgress(100);
  }
};


  const saveImportHistory = async (fileName, userEmail) => {
    console.log("saveImportHistory triggered:", { fileName, userEmail });

    try {
      const response = await axios.post(
        "https://server229256.nazwa.pl/save-import-history",
        {
          fileName,
          user: userEmail,
        },
        {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        }
      );

      if (response.status === 200) {
        console.log("Import history saved successfully.");
      } else {
        console.warn(
          "Unexpected response while saving history:",
          response.status
        );
      }
    } catch (error) {
      console.error("Error saving import history:", error.message);
    }
  };

  const postZakupData = async (bundle) => {
    const user = getUserFromToken();
    if (!user) {
      setMessage({
        text: "User not authenticated. Please log in.",
        isError: true,
      });
      return;
    }

    if (!bundle?.zakupy?.length) {
      console.warn("⚠️ postZakupData: empty zakupy for", bundle?.fileName);
      return;
    }

    // Summaries
    const sample = bundle.zakupy[0] || {};
    const hasK42 = bundle.zakupy.some((r) => r["K_42"] !== undefined);
    const hasK43 = bundle.zakupy.some((r) => r["K_43"] !== undefined);

    console.log("📦 postZakupData payload summary:", {
      fileName: bundle.fileName,
      rows: bundle.zakupy.length,
      dynamicColumns: bundle.zakupColumns,
      firstRowKeys: Object.keys(sample),
      firstRow: sample,
      hasK42,
      hasK43,
    });

    try {
      const resp = await axios.post(
        "https://server229256.nazwa.pl/save-zakup-data",
        {
          staticData: bundle.staticData,
          dynamicZakupData: bundle.zakupy,
          dynamicColumns: bundle.zakupColumns,
          fileName: bundle.fileName,
          user,
        },
        { headers: { Authorization: localStorage.getItem("token") } }
      );

      console.log("✅ /save-zakup-data response:", resp.status, resp.data);
      setMessage({
        text: `Purchases saved for "${bundle.fileName}".`,
        isError: false,
      });
    } catch (err) {
      console.error(
        "❌ /save-zakup-data error:",
        err?.response?.status,
        err?.response?.data || err.message
      );
      setMessage({
        text: `Błąd zapisu zakupów: ${
          err?.response?.data?.error || err.message
        }`,
        isError: true,
      });
    }
  };

return (
  <ImportWrapper>
    <Sidebar />

    <Content>
      {/* TopBar at the very top of the content column */}
      <TopBar/>


      {/* Scrollable page body */}
      <PageBody>
        <TabBar role="tablist" aria-label="Rodzaj importu">
          <TabButton
            type="button"
            role="tab"
            aria-selected={activeTab === "import"}
            $active={activeTab === "import"}
            onClick={() => setActiveTab("import")}
          >
            JPK VAT XML
          </TabButton>
          <TabButton
            type="button"
            role="tab"
            aria-selected={activeTab === "financial"}
            $active={activeTab === "financial"}
            onClick={() => setActiveTab("financial")}
          >
            Sprawozdanie finansowe XML
          </TabButton>
        </TabBar>

        {/* Import tab */}
        {activeTab === "import" && (
          <Card>
            <h2>Wgraj i zaimportuj pliki XML</h2>

            {/* Drag & drop area */}
            <div style={{ marginTop: "20px" }}>
<DropArea
  className={isDragOver ? "dragover" : ""}
  onDrop={handleDrop}
  onDragOver={handleDragOver}
  onDragLeave={handleDragLeave}
  onClick={() => {
    // ⛔ block when quota unknown/zero
    if (demoInfo.loading) {
      setMessage({ text: "Sprawdzam limit konta DEMO…", isError: false });
      return;
    }
    if (demoInfo.isDemo && demoInfo.left <= 0) {
      setMessage({
        text: `Limit importów DEMO został wykorzystany (${DEMO_MAX_IMPORTS}/${DEMO_MAX_IMPORTS}).`,
        isError: true,
      });
      return;
    }
    document.getElementById("file-upload").click();
  }}
  role="button"
  tabIndex={0}
  aria-label="Przeciągnij i upuść pliki XML lub kliknij, aby wybrać"
  onKeyDown={(e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (demoInfo.loading) {
        setMessage({ text: "Sprawdzam limit konta DEMO…", isError: false });
        return;
      }
      if (demoInfo.isDemo && demoInfo.left <= 0) {
        setMessage({
          text: `Limit importów DEMO został wykorzystany (${DEMO_MAX_IMPORTS}/${DEMO_MAX_IMPORTS}).`,
          isError: true,
        });
        return;
      }
      document.getElementById("file-upload").click();
    }
  }}
>
  <DropIcon aria-hidden="true" />
  <DropTitle>Przeciągnij i upuść pliki XML tutaj</DropTitle>
  <DropSubtitle>…albo kliknij, aby wybrać pliki z dysku</DropSubtitle>

  {!demoInfo.loading && demoInfo.isDemo && (
    <div
      style={{
        marginTop: 8,
        padding: "6px 10px",
        borderRadius: 8,
        background: "rgba(22,163,74,.08)",
        color: "#065f46",
        fontWeight: 700,
        fontSize: 13,
      }}
    >
      Pozostało importów: {demoInfo.left}/{DEMO_MAX_IMPORTS}
    </div>
  )}
</DropArea>

<input
  id="file-upload"
  type="file"
  multiple
  accept=".xml"
  onChange={handleFileChange}
  style={{ display: "none" }}
/>
            </div>

            {/* Messages */}
            {message && (
              <Message $isError={message.isError}>{message.text}</Message>
            )}

            {/* ✅ Ready files */}
            {filesToImport.some((f) => f.status === "ready") && (
              <div style={{ marginTop: "20px", paddingBottom: "20px" }}>
                <h4
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "green",
                    marginBottom: "12px",
                  }}
                >
                  <CheckCircle size={18} /> Pliki gotowe do importu:
                </h4>
                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {filesToImport
                    .filter((f) => f.status === "ready")
                    .map((f, idx) => (
                      <li
                        key={idx}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "10px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                          marginBottom: "10px",
                          background: "#fafafa",
                        }}
                      >
                        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <File size={16} />
                          <span style={{ fontWeight: 500 }}>{f.fileName}</span>
                        </span>
                        <span
                          style={{
                            color: "green",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <CheckCircle size={16} /> Gotowy
                        </span>
                      </li>
                    ))}
                </ul>
              </div>
            )}

            {/* ❌ Rejected files */}
            {filesToImport.some((f) => f.status !== "ready") && (
              <div style={{ marginTop: "20px", paddingBottom: "20px" }}>
                <h4
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "#b91c1c",
                    marginBottom: "12px",
                  }}
                >
                  <XCircle size={18} /> Pliki odrzucone:
                </h4>
                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {filesToImport
                    .filter((f) => f.status !== "ready")
                    .map((f, idx) => (
                      <li
                        key={idx}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "10px 12px",
                          border: "1px solid #f5c2c7",
                          borderRadius: "8px",
                          marginBottom: "10px",
                          background: "#fff7f7",
                        }}
                      >
                        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <File size={16} />
                          <span style={{ fontWeight: 500 }}>{f.fileName}</span>
                        </span>
                        <span
                          style={{
                            color: "#b91c1c",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <XCircle size={16} /> {f.error || "Błąd – nie będzie importowany"}
                        </span>
                      </li>
                    ))}
                </ul>
              </div>
            )}

            {/* Progress bar */}
            {isPosting && (
              <div style={{ textAlign: "center", margin: "20px 0" }}>
                <h3>Wysyłanie danych...</h3>
                <div
                  style={{
                    width: "100%",
                    height: "20px",
                    backgroundColor: "#e0e0e0",
                    borderRadius: "10px",
                    overflow: "hidden",
                    margin: "20px 0",
                  }}
                >
                  <div
                    style={{
                      width: `${progress}%`,
                      height: "100%",
                      backgroundColor: "#007bff",
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
                <p>
                  {Math.round(progress)}% zakończono ({currentFileIndex}/
                  {filesToImport.filter((f) => f.status === "ready").length} plików
                  prawidłowych)
                </p>
              </div>
            )}

            {/* Save button */}
            {!importCompleted && (
<Button
  onClick={handlePostData}
  disabled={
    isPosting ||
    demoInfo.loading ||
    (demoInfo.isDemo && demoInfo.left <= 0)
  }
  style={{ display: "flex", alignItems: "center", gap: "8px" }}
>
  <Save size={20} />
  {demoInfo.isDemo
    ? demoInfo.left > 0
      ? `Zapisz dane (pozostało: ${demoInfo.left}/${DEMO_MAX_IMPORTS})`
      : "Limit DEMO wykorzystany"
    : "Zapisz dane"}
</Button>
            )}

            {/* Cleanup state */}
            {importCompleted && (
              <Button disabled style={{ marginTop: "10px", opacity: 0.7 }}>
                <RefreshCcw size={18} className="spin" /> Czyszczenie... proszę czekać
              </Button>
            )}
          </Card>
        )}

        {/* Financial statement XML tab */}
        {activeTab === "financial" && <FinancialStatementImport />}

      </PageBody>
    </Content>
  </ImportWrapper>
);

};

export default ImportComponent;
